import {OptionalAccountField} from './fields/keyplay';
import {Market} from './market';
import {
  HubSpotIntegration,
  SalesforceIntegration,
  SyncType,
} from './integrations/crm';
import {Rollup} from './scoredAccounts';
import {OptionalScoringSignal} from './signals';
import {ObjectId} from 'bson';
import {
  WithId,
  WithRequired,
  WithTimestamp,
  ensure,
  keys,
} from '../shared/util';
import _ from 'lodash';
import {z} from 'zod';
import {zodTypeguard} from './api/helpers';
import {FieldDefinition} from './fields/types';
import {MetadataResponse} from './api/api';
import {ListBuilderActiveAccountsLimit} from './listBuilder';
import {
  SignalDefinition,
  AccountFieldOrCustomFieldSchema,
} from './signalDefinition';
import {Agent} from './agent';
import {ObjectIdSchema} from './zod';
import {LinkedInIntegration} from './integrations/linkedin';
import {SlackIntegration} from './integrations/slack';
import {ApiKey} from './api';
import {IntentSource} from './intent';
import {WebIntentIntegration} from './integrations/webIntent';

const CustomerOptionsSchema = z.object({
  // For demo accounts we'd prefer not to keep mutations.
  skipMutations: z.boolean().optional(),

  // Don't allow an account to be wiped by a script unless we
  // explicitly mark it as safe. Generally should only be set
  // manually right before a wipe, or on internal accounts that
  // we expect to be wiped often.
  safeToWipe: z.boolean().optional(),

  // Whether we add accounts enriched through the API.
  addOnEnrich: z.boolean().optional(),

  // For some customers we only want to pull/sync new CRM records (those created after
  // their integration auth date)
  onlyPullAndSyncNewCrmRecords: z.boolean().optional(),
});
export type CustomerOptions = z.infer<typeof CustomerOptionsSchema>;
export const isCustomerOptions = zodTypeguard(CustomerOptionsSchema);

const SettableCustomerOptionsSchema = CustomerOptionsSchema.pick({
  addOnEnrich: true,
}).keyof();
export const isSettableCustomerOption = zodTypeguard(
  SettableCustomerOptionsSchema
);

// Toggles are meant to be temporary flags that can be used to enable
// certain functionality, regardless of plan.
// When you remove a Toggle, remember to run the following script to clean
// up database entries
// npm run remove-invalid-customer-settings
export const Toggles = [] as const;
const ToggleSchema = z.enum([
  // a placeholder toggle to make the type non-empty
  'dummyToggle',
  ...Toggles,
]);
export type Toggle = z.infer<typeof ToggleSchema>;

export function hasToggle(
  customer: Pick<WithId<Customer>, 'toggles'>,
  toggle: Toggle
) {
  return !!customer.toggles?.includes(toggle);
}

// Features are generally whole product experiences that are available
// for customers on certain plans. Certain plans allow for customization
// so not all customers on the same plan will have the same features.
//
// When you remove a FeatureName, remember to run the following script to clean
// up database entries: npm run remove-invalid-customer-settings
export const FeatureNames = [
  'apiKey',
  'freemiumV3',
  'hubspot',
  'importsTab',
  'intent',
  'lookalikes',
  'modelTester',
  'salesforce',
  'selectiveSync',
] as const;
const FeatureSchema = z.enum(FeatureNames);
export type FeatureName = z.infer<typeof FeatureSchema>;

// Capabilities are available to all customers of a specific plan.
// They are not customizable.
export const Capabilities = ['zeroWeightSignals'] as const;
const CapabilitySchema = z.enum(Capabilities);
export type Capability = z.infer<typeof CapabilitySchema>;

const ListBuilderPlanSchema = z.object({
  type: z.literal('listBuilder'),
  endDate: z.date(),
  trial: z.boolean().optional(),
  downgradeReminderSentAt: z.date().optional(),
});
export type ListBuilderPlan = z.infer<typeof ListBuilderPlanSchema>;

export const PlanSchema = z.union([
  z.object({
    type: z.union([
      z.literal('poc'),
      z.literal('free'),
      z.literal('startup'),
      z.literal('startupPlus'),
      z.literal('growth'),
      z.literal('scale'),
      z.literal('custom'),
    ]),
  }),
  ListBuilderPlanSchema,
]);
export type Plan = z.infer<typeof PlanSchema>;
export type PlanType = Plan['type'];

export const PlanLabels = {
  poc: 'POC',
  free: 'Free',
  listBuilder: 'List Builder',
  startup: 'Startup',
  startupPlus: 'Startup Plus',
  growth: 'Growth',
  scale: 'Scale',
  custom: 'Custom',
} as const satisfies Record<PlanType, string>;
export const PlanTypes = keys(PlanLabels);

const LimitsSchema = z.object({
  activeAccounts: z.number(),
  apiCallsPerMonth: z.number(),
  lookalikes: z.number(),
  recommendedAccounts: z.number(),
  recommendedAccountsSearch: z.number(),
  saves: z.number().optional(),
  savesPerMonth: z.number().optional(),
  similarAccountsSearch: z.number(),
  users: z.number(),
  features: FeatureSchema.array(),
  capabilities: CapabilitySchema.array(),
  markets: z.number(),
  webIntentIdentificationsPerMonth: z.number(),

  // free-only
  marketPublishes: z.number().optional(),
});
export type Limits = z.infer<typeof LimitsSchema>;

const LimitKeys = LimitsSchema.keyof();
export type Limit = z.infer<typeof LimitKeys>;

const StaticLimitSchema = LimitKeys.extract([
  'activeAccounts',
  'lookalikes',
  'recommendedAccounts',
  'recommendedAccountsSearch',
  'similarAccountsSearch',
  'users',
  'markets',
]);
export type StaticLimit = z.infer<typeof StaticLimitSchema>;

const TrackableLimitSchema = LimitKeys.extract([
  'marketPublishes',
  'saves',
  'savesPerMonth',
  'apiCallsPerMonth',
  'webIntentIdentificationsPerMonth',
]);
export type TrackableLimit = z.infer<typeof TrackableLimitSchema>;

// TODO: Investigate if this can be simplified
const CustomerLimitsSchema = z.object(
  Object.fromEntries([
    ...StaticLimitSchema.options.map((key) => [key, z.number().optional()]),
    ...TrackableLimitSchema.options.map((key) => [
      key,
      z
        .object({
          limit: z.number().optional(),
          value: z.number().optional(),
        })
        .optional(),
    ]),
  ])
);
export type CustomerLimits = z.infer<typeof CustomerLimitsSchema>;

const listBuilderRecommendedAccounts: LimitFn = (customer) => {
  const limit = customer.limits?.saves?.limit ?? 0;
  const value = customer.limits?.saves?.value ?? 0;
  return _.clamp((limit - value) * 2, 100, 5_000);
};

const listBuilderRecommendedAccountsSearch: LimitFn = (customer) =>
  _.clamp(listBuilderRecommendedAccounts(customer) * 2, 1_000, 10_000);

type LimitFn = (customer: MetadataResponse['customer']) => number;
type FeaturesFn = (customer: MetadataResponse['customer']) => FeatureName[];

export const MaxLimits: Partial<Record<StaticLimit, number>> = {
  activeAccounts: 250_000,
  recommendedAccounts: 50_000,
  lookalikes: 2_500,
  markets: 5,
};

export const PlanToPlanLimits: {
  [P in PlanType]: {
    limits: {
      [L in keyof Limits]: L extends Exclude<Limit, 'features' | 'capabilities'>
        ? Limits[L] | LimitFn
        : L extends 'features'
          ? Limits[L] | FeaturesFn
          : Limits[L];
    };
    overridableLimits: Limit[];
  };
} = {
  poc: {
    limits: {
      activeAccounts: 50_000,
      lookalikes: 250,
      recommendedAccounts: 10_000,
      recommendedAccountsSearch: 10_000,
      similarAccountsSearch: 10_000,
      users: 5,
      apiCallsPerMonth: 0,
      markets: 3,
      webIntentIdentificationsPerMonth: 1_000,
      features: [
        'apiKey',
        'hubspot',
        'importsTab',
        'intent',
        'modelTester',
        'salesforce',
        'selectiveSync',
      ],
      capabilities: ['zeroWeightSignals'],
    },
    overridableLimits: ['markets'],
  },
  free: {
    limits: {
      activeAccounts: 100,
      lookalikes: 20,
      recommendedAccounts: 50,
      recommendedAccountsSearch: 1_000,
      saves: 25,
      similarAccountsSearch: 1_000,
      users: 10,
      features: (customer) => {
        return customer.features?.includes('freemiumV3')
          ? ['freemiumV3', 'intent']
          : [];
      },
      capabilities: [],
      apiCallsPerMonth: 0,
      markets: 1,
      webIntentIdentificationsPerMonth: 25,

      // free specific
      marketPublishes: 10,
    },
    overridableLimits: [],
  },
  listBuilder: {
    limits: {
      activeAccounts: ListBuilderActiveAccountsLimit,
      lookalikes: 50,
      recommendedAccounts: listBuilderRecommendedAccounts,
      recommendedAccountsSearch: listBuilderRecommendedAccountsSearch,
      similarAccountsSearch: listBuilderRecommendedAccountsSearch,
      users: 10,
      features: (customer) =>
        (customer.plan as ListBuilderPlan).trial ? [] : ['hubspot'],
      capabilities: [],
      apiCallsPerMonth: 0,
      markets: 1,
      webIntentIdentificationsPerMonth: 25,
    },
    overridableLimits: ['saves'],
  },
  startup: {
    limits: {
      activeAccounts: 5_000,
      lookalikes: 50,
      recommendedAccounts: 10_000,
      recommendedAccountsSearch: 10_000,
      similarAccountsSearch: 10_000,
      users: 25,
      features: ['hubspot', 'intent'],
      capabilities: [],
      apiCallsPerMonth: 0,
      markets: 1,
      webIntentIdentificationsPerMonth: 1_000,
    },
    overridableLimits: [],
  },
  startupPlus: {
    limits: {
      activeAccounts: 20_000,
      lookalikes: 50,
      recommendedAccounts: 10_000,
      recommendedAccountsSearch: 10_000,
      similarAccountsSearch: 10_000,
      users: 25,
      features: ['hubspot', 'importsTab', 'intent'],
      capabilities: ['zeroWeightSignals'],
      apiCallsPerMonth: 0,
      markets: 1,
      webIntentIdentificationsPerMonth: 1_000,
    },
    overridableLimits: [],
  },
  growth: {
    limits: {
      activeAccounts: 75_000,
      lookalikes: 250,
      recommendedAccounts: 10_000,
      recommendedAccountsSearch: 10_000,
      similarAccountsSearch: 10_000,
      users: 100,
      apiCallsPerMonth: 0,
      markets: 1,
      webIntentIdentificationsPerMonth: 1_000,
      features: [
        'hubspot',
        'importsTab',
        'intent',
        'modelTester',
        'salesforce',
      ],
      capabilities: ['zeroWeightSignals'],
    },
    overridableLimits: ['activeAccounts'],
  },
  scale: {
    limits: {
      activeAccounts: 100_000,
      lookalikes: 250,
      recommendedAccounts: 10_000,
      recommendedAccountsSearch: 10_000,
      similarAccountsSearch: 10_000,
      users: 100,
      apiCallsPerMonth: 5_000,
      markets: 2,
      webIntentIdentificationsPerMonth: 1_000,
      features: [
        'hubspot',
        'importsTab',
        'intent',
        'modelTester',
        'salesforce',
      ],
      capabilities: ['zeroWeightSignals'],
    },
    overridableLimits: ['activeAccounts', 'features', 'lookalikes', 'markets'],
  },
  custom: {
    limits: {
      activeAccounts: 50_000,
      lookalikes: 250,
      recommendedAccounts: 10_000,
      recommendedAccountsSearch: 10_000,
      similarAccountsSearch: 10_000,
      users: 100,
      apiCallsPerMonth: 5_000,
      markets: 1,
      webIntentIdentificationsPerMonth: 1_000,
      features: [
        'hubspot',
        'importsTab',
        'intent',
        'modelTester',
        'salesforce',
      ],
      capabilities: ['zeroWeightSignals'],
    },
    overridableLimits: [
      'activeAccounts',
      'apiCallsPerMonth',
      'features',
      'lookalikes',
      'markets',
      'recommendedAccounts',
      'recommendedAccountsSearch',
      'savesPerMonth',
      'webIntentIdentificationsPerMonth',
    ],
  },
};

export function getSupportedSyncTypes(
  customer: Pick<Customer, 'plan' | 'features'>
): SyncType[] {
  switch (customer.plan.type) {
    case 'free':
      return [];
    case 'listBuilder':
    case 'startup':
      return ['oneWay'];
    case 'startupPlus':
    case 'growth':
      return ['oneWay', 'twoWay'];
    case 'poc':
    case 'scale':
    case 'custom':
      return customer.features?.includes('selectiveSync')
        ? ['oneWay', 'twoWay', 'selectiveTwoWay']
        : ['oneWay', 'twoWay'];
  }
}

export function getFeatures(
  customer: MetadataResponse['customer']
): FeatureName[] {
  const features = PlanToPlanLimits[customer.plan.type].limits.features;
  if (_.isFunction(features)) {
    return features(customer);
  }

  const overridableLimits =
    PlanToPlanLimits[customer.plan.type].overridableLimits;
  if (overridableLimits.includes('features')) {
    return customer.features ?? features;
  }
  return features;
}

export function isCapabilityEnabled(
  customer: Pick<Customer, 'plan'>,
  capability: Capability
) {
  return PlanToPlanLimits[customer.plan.type].limits.capabilities.includes(
    capability
  );
}

export function getCurrentValue(
  customer: Pick<WithId<Customer>, 'limits'>,
  limitName: TrackableLimit
): number {
  return customer.limits?.[limitName]?.value ?? 0;
}

export function getRemaining(
  customer: MetadataResponse['customer'],
  limitName: TrackableLimit
): number {
  const limit = getLimit(customer, limitName);
  if (limit === undefined) {
    return Infinity;
  }

  return limit - getCurrentValue(customer, limitName);
}

// Helper functions to get the relevent saves limit for a customer
export function getSavesLimitName(customer: MetadataResponse['customer']) {
  return customer.plan.type === 'free' || customer.plan.type === 'listBuilder'
    ? 'saves'
    : 'savesPerMonth';
}

export function getSavesQuota(customer: MetadataResponse['customer']) {
  const limitName = getSavesLimitName(customer);
  const limit = getLimit(customer, limitName);

  // need to handle cases where customer might have downgraded from freemium, and
  // 'current' is greater than limit
  return {
    limit,
    current: Math.min(limit ?? Infinity, getCurrentValue(customer, limitName)),
    remaining: Math.max(0, getRemaining(customer, limitName)),
  };
}

export function getLimit<L extends Exclude<Limit, 'features' | 'capabilities'>>(
  customer: MetadataResponse['customer'],
  limitName: L
): Limits[L] {
  const limitOrFn = PlanToPlanLimits[customer.plan.type].limits[limitName];
  const planLimit = _.isFunction(limitOrFn)
    ? limitOrFn(customer)
    : (limitOrFn as Limits[L]);

  const overridableLimits =
    PlanToPlanLimits[customer.plan.type].overridableLimits;
  // If the limit is not overridable, return the default plan limit
  if (!overridableLimits.includes(limitName)) {
    return planLimit;
  }

  const customerLimit = customer.limits?.[limitName];
  // Check for static limits first
  if (_.isNumber(customerLimit)) {
    const maxLimit = MaxLimits[limitName as StaticLimit];
    return Math.min(customerLimit, maxLimit ?? customerLimit);
  }

  // Otherwise, check for a limit override and fallback to the plan limit
  if (customerLimit?.limit !== undefined) {
    return customerLimit.limit;
  }

  return planLimit;
}

export function isLimitExceeded(
  customer: MetadataResponse['customer'],
  limitName: TrackableLimit,
  increaseBy = 1
): boolean {
  const limit = getLimit(customer, limitName) ?? Infinity;
  const newValue = getCurrentValue(customer, limitName) + increaseBy;

  return newValue > limit;
}

const CreditTransactionBaseSchema = z.object({
  customerId: z.instanceof(ObjectId),
  timestamp: z.date(),
  amount: z.number(),
  description: z.string().optional(),
  type: z.string(),
});

type CreditTransactionBase = z.infer<typeof CreditTransactionBaseSchema>;

const CreditPurchaseSchema = CreditTransactionBaseSchema.extend({
  type: z.literal('purchase'),
  initiatedBy: z.string(),
});
export type CreditPurchase = z.infer<typeof CreditPurchaseSchema>;

const TaskSourceSchema = z.object({
  type: z.literal('task'),
  taskId: ObjectIdSchema,
});

const ApiSourceSchema = z.object({
  type: z.literal('api'),
  apiRequestId: ObjectIdSchema,
});

const CreditRedemptionSourceSchema = z.union([
  TaskSourceSchema,
  ApiSourceSchema,
]);
export type CreditRedemptionSource = z.infer<
  typeof CreditRedemptionSourceSchema
>;

const AccountEnrichmentSchema = z.object({
  type: z.literal('accountEnrichment'),
  fieldId: ObjectIdSchema,
  numAccounts: z.number(),
});

const FieldEnrichmentSchema = z.object({
  type: z.literal('fieldEnrichment'),
  fieldIds: ObjectIdSchema.array(),
});

const CreditRedemptionPurposeSchema = z.union([
  AccountEnrichmentSchema,
  FieldEnrichmentSchema,
]);
export type CreditRedemptionPurpose = z.infer<
  typeof CreditRedemptionPurposeSchema
>;

const CreditRedemptionSchema = CreditTransactionBaseSchema.extend({
  estimatedAmount: z.number(),
  state: z.enum(['pending', 'completed']),
  type: z.literal('redemption'),
  source: CreditRedemptionSourceSchema,
  purpose: CreditRedemptionPurposeSchema,
});

export type CreditRedemption = z.infer<typeof CreditRedemptionSchema>;

export const AccountEnrichmentCreditRedemptionSchema =
  CreditRedemptionSchema.extend({
    purpose: AccountEnrichmentSchema,
  });
export type AccountEnrichmentCreditRedemption = z.infer<
  typeof AccountEnrichmentCreditRedemptionSchema
>;

// Used for manually updating customer credits (e.g. by support)
export type ManualCreditTransaction = WithRequired<
  CreditTransactionBase,
  'description'
> & {
  initiatedBy: string;
  type: 'manual';
};

export type CreditTransaction =
  | CreditPurchase
  | CreditRedemption
  | ManualCreditTransaction;

export const IntentModelSchema = z.object({
  id: ObjectIdSchema,
  label: z.string(),
  fieldsWithWeights: z.array(
    z.object({
      field: AccountFieldOrCustomFieldSchema,
      weight: z.number(),
    })
  ),
  thresholds: z.object({
    low: z.number(),
    medium: z.number(),
    high: z.number(),
  }),
});
export type IntentModel = z.infer<typeof IntentModelSchema>;
export const isIntentModel = zodTypeguard(IntentModelSchema);

const IcpFactorSchema = z.object({
  findings: z.string(),
  weight: z.enum(['high', 'moderate', 'low']),
});

export const IcpDefinitionSchema = z.object({
  overview: z.string(),
  matrix: z.object({
    firmographics: IcpFactorSchema,
    situation: IcpFactorSchema,
    organization: IcpFactorSchema,
    technology: IcpFactorSchema,
  }),
  themes: z
    .object({
      theme: z.string(),
      observation: z.string(),
    })
    .array(),
});
export type IcpDefinition = z.infer<typeof IcpDefinitionSchema>;

export type Customer = {
  timestamp: Date;
  name: string;
  website?: {
    domain: string;
    accountId?: ObjectId;
    whatISell?: string;
  };
  icpDefinition?: WithTimestamp<IcpDefinition>;
  baseId?: string;
  activeMarketId?: ObjectId;
  markets: Market[];
  fields: OptionalAccountField[];
  signals: OptionalScoringSignal[];
  rollups: Rollup[];
  integrations: {
    hubspot?: HubSpotIntegration;
    salesforce?: SalesforceIntegration;
    linkedin?: LinkedInIntegration;
    slack?: SlackIntegration;
    webIntent?: WebIntentIntegration;
  };

  apiKeys: ApiKey[];
  credits: number;
  limits?: CustomerLimits;

  options?: CustomerOptions;
  features?: FeatureName[];
  toggles?: Toggle[];
  plan: Plan;
  onboarded: boolean;
  // Only used for freemium customers
  firstIntentAccountsEmailSentAt?: Date;

  transactions: CreditTransaction[];
  agents?: Agent[];
  fieldDefinitions?: FieldDefinition[];
  intentSources?: IntentSource[];
  intentModels?: IntentModel[];
  signalDefinitions?: SignalDefinition[];

  // Churn tracking
  churnedAt?: Date;
  scheduledDeletionAt?: Date;
};

const CreateCustomerRequestSchema = z.object({
  name: z.string(),
  plan: PlanSchema,
  limits: CustomerLimitsSchema.optional(),
  features: FeatureSchema.array().optional(),
  toggles: ToggleSchema.array().optional(),
  onboarded: z.boolean(),
});
export type CreateCustomerRequest = z.infer<typeof CreateCustomerRequestSchema>;
export const isCreateCustomerRequest = zodTypeguard(
  CreateCustomerRequestSchema
);

const EditCustomerRequestSchema = CreateCustomerRequestSchema.extend({
  id: z.instanceof(ObjectId),
});
export type EditCustomerRequest = z.infer<typeof EditCustomerRequestSchema>;
export const isEditCustomerRequest = zodTypeguard(EditCustomerRequestSchema);

const DeleteCustomerRequestSchema = z.object({
  customerId: z.instanceof(ObjectId),
});
export const isDeleteCustomerRequest = zodTypeguard(
  DeleteCustomerRequestSchema
);

export function getActiveMarket(customer: Customer) {
  const activeMarketId = customer.activeMarketId ?? customer.markets[0].id;
  return ensure(
    customer.markets.find((market) => market.id.equals(activeMarketId))
  );
}
